import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import { Plus, LogOut, FolderKanban, Calendar, Loader2, Settings, BookTemplate, BarChart3, Users, Mail, Menu, X, MoreVertical, Pencil, Trash2, Search, Filter, Clock, CheckCircle, Heart, AlertCircle, Download } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import DashboardStats from "../components/DashboardStats";
import NotificationBell from "../components/NotificationBell";
import GlobalSearch from "../components/GlobalSearch";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ThemeToggle from "../components/ThemeToggle";
import UpgradeModal from "../components/UpgradeModal";
import { useTranslation } from 'react-i18next';

function Dashboard() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  const [formData, setFormData] = useState({ 
    name: "", 
    description: "",
    employer: "",
    upper_employer: "",
    main_contractor: ""
  });
  const [editFormData, setEditFormData] = useState({ 
    name: "", 
    description: "",
    employer: "",
    upper_employer: "",
    main_contractor: ""
  });
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, owned, member
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get("/projects");
      setProjects(response.data);
    } catch (error) {
      toast.error(t('projectsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Filtreleme ve sıralama - useMemo ile optimize edildi
  const filteredAndSortedProjects = useMemo(() => {
    let filtered = [...projects];

    // Kullanıcı bazlı filtreleme
    if (filterType === "owned" && user) {
      filtered = filtered.filter(p => p.owner_id === user.id);
    } else if (filterType === "member" && user) {
      filtered = filtered.filter(p => 
        p.members && p.members.some(m => m.user_id === user.id)
      );
    }

    // Arama
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(query) ||
        (p.description && p.description.toLowerCase().includes(query)) ||
        (p.employer && p.employer.toLowerCase().includes(query))
      );
    }

    // Sondan başa sıralama (en yeni üstte)
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA; // Descending order
    });

    return filtered;
  }, [projects, filterType, user, searchQuery]);

  const handleCreateProject = useCallback(async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post("/projects", formData);
      toast.success(t('projectCreated'));
      setDialogOpen(false);
      setFormData({ 
        name: "", 
        description: "",
        employer: "",
        upper_employer: "",
        main_contractor: ""
      });
      fetchProjects();
    } catch (error) {
      // Check if it's a limit error
      if (error.response?.status === 403 && error.response?.data?.detail?.limit_type) {
        const limitData = error.response.data.detail;
        setLimitInfo({
          type: limitData.limit_type,
          current: limitData.current,
          limit: limitData.limit,
          planName: limitData.plan_name
        });
        setUpgradeModalOpen(true);
        setDialogOpen(false);
      } else {
        toast.error(t('projectCreateFailed'));
      }
    }
  }, [formData, t]);

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setEditFormData({
      name: project.name || "",
      description: project.description || "",
      employer: project.employer || "",
      upper_employer: project.upper_employer || "",
      main_contractor: project.main_contractor || ""
    });
    setEditDialogOpen(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.put(`/projects/${selectedProject.id}`, editFormData);
      toast.success(t('projectUpdated'));
      setEditDialogOpen(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('noUpdatePermission'));
      } else {
        toast.error(t('projectUpdateFailed'));
      }
    }
  };

  const handleDeleteProject = (project) => {
    setSelectedProject(project);
    setDeleteConfirmation("");
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmation !== selectedProject.name) {
      toast.error(t('projectNameMismatch'));
      return;
    }

    try {
      await axiosInstance.delete(`/projects/${selectedProject.id}`);
      toast.success(t('projectDeleted'));
      setDeleteDialogOpen(false);
      setSelectedProject(null);
      setDeleteConfirmation("");
      fetchProjects();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Bu projeyi silme yetkiniz yok");
      } else {
        toast.error("Proje silinemedi");
      }
    }
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  }, [navigate]);

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-3">
          {/* Desktop Header */}
          <div className="hidden md:flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img 
                src="/milestora-logo-light.png" 
                alt="Milestora" 
                className="h-10 w-auto object-contain"
              />
              <div>
                <h1 className="text-lg font-bold" style={{ fontFamily: 'Manrope', color: '#1F2937' }}>
                  {t('dashboard')}
                </h1>
                <p className="text-xs text-gray-500">{user?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <GlobalSearch />
              <ThemeToggle />
              <LanguageSwitcher />
              <NotificationBell />
              
              {user?.role === "admin" && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate("/users")}
                    title={t('userManagement')}
                  >
                    <Users className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate("/smtp-settings")}
                    title="SMTP"
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                </>
              )}
              
              {user?.role === "supervisor" && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigate("/supervisor")}
                  title={t('supervisorPanel')}
                >
                  <Users className="h-4 w-4" />
                </Button>
              )}
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/reports")}
                title={t('reports')}
              >
                <BarChart3 className="h-4 w-4" />
              </Button>

              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/health-status")}
                title="Sağlık Durumu"
                className="bg-green-50 hover:bg-green-100"
              >
                <Heart className="h-4 w-4 text-green-600" />
              </Button>

              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/task-aging")}
                title="Görev Yaşlandırma"
                className="bg-orange-50 hover:bg-orange-100"
              >
                <AlertCircle className="h-4 w-4 text-orange-600" />
              </Button>

              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/export")}
                title="Rapor Export"
                className="bg-blue-50 hover:bg-blue-100"
              >
                <Download className="h-4 w-4 text-blue-600" />
              </Button>

              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/sprints")}
                title={t('sprintManagement')}
              >
                <Calendar className="h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/resource-search")}
                title={t('resourceSearch')}
              >
                <Users className="h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/timesheets")}
                title={t('timesheets')}
              >
                <Clock className="h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/templates")}
                title={t('templates')}
              >
                <BookTemplate className="h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/settings")}
                title={t('settings')}
              >
                <Settings className="h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={handleLogout}
                title={t('logout')}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Mobile Header */}
          <div className="md:hidden flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <img 
                src="/milestora-logo-light.png" 
                alt="Milestora" 
                className="h-8 w-auto object-contain flex-shrink-0"
              />
              <span className="text-base font-bold truncate" style={{ fontFamily: 'Manrope' }}>
                {t('dashboard')}
              </span>
            </div>
            
            <div className="flex items-center gap-1 flex-shrink-0">
              <NotificationBell />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-3 pb-3 border-t pt-3 space-y-3">
              <div className="px-2">
                <GlobalSearch />
              </div>
              
              <div className="flex items-center justify-between px-2">
                <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{user?.name}</span>
                <div className="flex items-center gap-2">
                  <ThemeToggle />
                  <LanguageSwitcher />
                </div>
              </div>
              
              {user?.role === "admin" && (
                <>
                  <button
                    onClick={() => { navigate("/users"); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Users className="h-4 w-4" />
                    <span>{t('userManagement')}</span>
                  </button>
                  <button
                    onClick={() => { navigate("/smtp-settings"); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    <span>SMTP Settings</span>
                  </button>
                </>
              )}
              
              {user?.role === "supervisor" && (
                <button
                  onClick={() => { navigate("/supervisor"); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Users className="h-4 w-4" />
                  <span>{t('supervisorPanel')}</span>
                </button>
              )}
              
              <button
                onClick={() => { navigate("/sprints"); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Calendar className="h-4 w-4" />
                <span>{t('sprintManagement')}</span>
              </button>
              
              <button
                onClick={() => { navigate("/resource-search"); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Users className="h-4 w-4" />
                <span>{t('resourceSearch')}</span>
              </button>
              
              <button
                onClick={() => { navigate("/reports"); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <BarChart3 className="h-4 w-4" />
                <span>{t('reports')}</span>
              </button>

              <button
                onClick={() => { navigate("/health-status"); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-green-50 rounded-lg transition-colors"
              >
                <Heart className="h-4 w-4 text-green-600" />
                <span>Sağlık Durumu</span>
              </button>

              <button
                onClick={() => { navigate("/task-aging"); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-orange-50 rounded-lg transition-colors"
              >
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <span>Görev Yaşlandırma</span>
              </button>

              <button
                onClick={() => { navigate("/export"); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Download className="h-4 w-4 text-blue-600" />
                <span>Rapor Export</span>
              </button>

              <button
                onClick={() => { navigate("/timesheets"); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Clock className="h-4 w-4" />
                <span>{t('timesheets')}</span>
              </button>
              
              <button
                onClick={() => { navigate("/templates"); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <BookTemplate className="h-4 w-4" />
                <span>{t('templates')}</span>
              </button>
              
              <button
                onClick={() => { navigate("/settings"); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Settings className="h-4 w-4" />
                <span>{t('settings')}</span>
              </button>
              
              <button
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span>{t('logout')}</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Dashboard Stats & Projects Grid */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 space-y-4 sm:space-y-8">
        {/* Dashboard Stats */}
        <DashboardStats />

        {/* Projects Section */}
        <div>
          <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
            <span>{t('projects')}</span>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-1 sm:gap-2 text-sm sm:text-base px-3 sm:px-4" style={{ background: '#3B82F6' }} data-testid="create-project-button">
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('newProject')}</span>
                  <span className="sm:hidden">Yeni</span>
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="create-project-dialog">
                <DialogHeader>
                  <DialogTitle>{t('createProject')}</DialogTitle>
                  <DialogDescription>
                    {t('projectDescription')}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateProject} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('projectName')}</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t('projectName')}
                      required
                      data-testid="project-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">{t('description')}</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder={t('projectDescription')}
                      rows={3}
                      data-testid="project-description-input"
                    />
                  </div>
                  
                  {/* Yeni Opsiyonel Alanlar (New Optional Fields) */}
                  <div className="space-y-2">
                    <Label htmlFor="employer">{t('employer')} ({t('optional')})</Label>
                    <Input
                      id="employer"
                      value={formData.employer || ''}
                      onChange={(e) => setFormData({ ...formData, employer: e.target.value })}
                      placeholder={t('employerPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="upper_employer">{t('upperEmployer')} ({t('optional')})</Label>
                    <Input
                      id="upper_employer"
                      value={formData.upper_employer || ''}
                      onChange={(e) => setFormData({ ...formData, upper_employer: e.target.value })}
                      placeholder={t('upperEmployerPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="main_contractor">{t('mainContractor')} ({t('optional')})</Label>
                    <Input
                      id="main_contractor"
                      value={formData.main_contractor || ''}
                      onChange={(e) => setFormData({ ...formData, main_contractor: e.target.value })}
                      placeholder={t('mainContractorPlaceholder')}
                    />
                  </div>
                  
                  <Button type="submit" className="w-full" style={{ background: '#3B82F6' }} data-testid="submit-project-button">
                    {t('create')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </h2>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-16" data-testid="no-projects-message">
              <FolderKanban className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('noProjects')}</h3>
              <p className="text-gray-500 mb-6">{t('projectDescription')}</p>
              <Button onClick={() => setDialogOpen(true)} style={{ background: '#3B82F6' }}>
                <Plus className="h-4 w-4 mr-2" />
                {t('newProject')}
              </Button>
            </div>
          ) : (
            <>
              {/* Search and Filter Section */}
              <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
                {/* Search Input */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder={t('searchProjects')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 text-sm sm:text-base h-9 sm:h-10"
                  />
                </div>

                {/* Filter Dropdown */}
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-full sm:w-[200px] text-sm sm:text-base h-9 sm:h-10">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder={t('filterProjects')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allProjects')}</SelectItem>
                    <SelectItem value="owned">{t('myProjects')}</SelectItem>
                    <SelectItem value="member">{t('memberProjects')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Projects Grid */}
              {filteredAndSortedProjects.length === 0 ? (
                <div className="text-center py-16">
                  <FolderKanban className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('noProjectsFound')}</h3>
                  <p className="text-gray-500">{t('tryDifferentSearch')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="projects-grid">
                {filteredAndSortedProjects.map((project) => (
              <Card
                key={project.id}
                className="hover:shadow-lg transition-shadow bg-white border border-gray-200 relative group"
                data-testid={`project-card-${project.id}`}
              >
                {/* Edit/Delete menu - for owner, company admin, or supervisor */}
                {user && (project.owner_id === user.id || user.role === 'admin' || user.role === 'supervisor') && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`project-menu-${project.id}`}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditProject(project);
                        }}
                        data-testid={`edit-project-${project.id}`}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        {t('editProject')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project);
                        }}
                        className="text-red-600"
                        data-testid={`delete-project-${project.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('deleteProject')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <div 
                  className="cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Manrope' }}>
                      <FolderKanban className="h-5 w-5 text-blue-500" />
                      {project.name}
                    </CardTitle>
                    <CardDescription className="line-clamp-2 min-h-[40px]">
                      {project.description || t('noDescription')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-500 gap-2">
                        <Calendar className="h-4 w-4" />
                        <span className="text-xs text-gray-400">{t('created')}:</span>
                        {format(new Date(project.created_at), "d MMM yyyy", { locale: tr })}
                      </div>
                      {project.earliest_start_date && (
                        <div className="flex items-center text-sm gap-2">
                          {project.start_date_type === 'actual' ? (
                            <>
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="text-xs font-medium text-green-700">{t('actualStart')}:</span>
                              <span className="text-green-700">
                                {format(new Date(project.earliest_start_date), "d MMM yyyy", { locale: tr })}
                              </span>
                            </>
                          ) : (
                            <>
                              <Clock className="h-4 w-4 text-blue-600" />
                              <span className="text-xs font-medium text-blue-700">{t('estimatedStart')}:</span>
                              <span className="text-blue-700">
                                {format(new Date(project.earliest_start_date), "d MMM yyyy", { locale: tr })}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                      {project.employer && (
                        <div className="flex items-center text-sm text-gray-600 gap-2">
                          <span className="font-medium text-xs">{t('employer')}:</span>
                          <span className="truncate">{project.employer}</span>
                        </div>
                      )}
                      {project.members && project.members.length > 0 && (
                        <div className="flex items-center text-sm text-gray-600 gap-2">
                          <Users className="h-4 w-4" />
                          <span>{project.members.length} {t('members')}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
            </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{t('editProject')}</DialogTitle>
            <DialogDescription>
              {t('editProjectDescription')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateProject}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">{t('projectName')}</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-description">{t('projectDescription')}</Label>
                <Textarea
                  id="edit-description"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-employer">{t('employer')}</Label>
                <Input
                  id="edit-employer"
                  value={editFormData.employer}
                  onChange={(e) => setEditFormData({ ...editFormData, employer: e.target.value })}
                  placeholder={t('employerPlaceholder')}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-upper-employer">{t('upperEmployer')}</Label>
                <Input
                  id="edit-upper-employer"
                  value={editFormData.upper_employer}
                  onChange={(e) => setEditFormData({ ...editFormData, upper_employer: e.target.value })}
                  placeholder={t('upperEmployerPlaceholder')}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-main-contractor">{t('mainContractor')}</Label>
                <Input
                  id="edit-main-contractor"
                  value={editFormData.main_contractor}
                  onChange={(e) => setEditFormData({ ...editFormData, main_contractor: e.target.value })}
                  placeholder={t('mainContractorPlaceholder')}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" style={{ background: '#3B82F6' }}>
                {t('saveChanges')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Project Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">{t('deleteProjectTitle')}</DialogTitle>
            <DialogDescription>
              {t('deleteProjectWarning')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800 font-semibold mb-2">
                {t('deleteProjectImpact')}
              </p>
              <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                <li>{t('allTasksWillBeDeleted')}</li>
                <li>{t('allSubtasksWillBeDeleted')}</li>
                <li>{t('allCommentsWillBeDeleted')}</li>
                <li>{t('allTimeLogsWillBeDeleted')}</li>
                <li>{t('allNotificationsWillBeDeleted')}</li>
                <li>{t('allDocumentsWillBeDeleted')}</li>
              </ul>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="delete-confirmation">
                {t('typeProjectNameToConfirm')}: <span className="font-bold">{selectedProject?.name}</span>
              </Label>
              <Input
                id="delete-confirmation"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder={selectedProject?.name}
                className="border-red-300 focus:border-red-500"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteConfirmation("");
            }}>
              {t('cancel')}
            </Button>
            <Button 
              type="button" 
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteConfirmation !== selectedProject?.name}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('deleteProjectPermanently')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Modal */}
      {limitInfo && (
        <UpgradeModal
          open={upgradeModalOpen}
          onClose={() => setUpgradeModalOpen(false)}
          limitType={limitInfo.type}
          currentUsage={limitInfo.current}
          limit={limitInfo.limit}
          planName={limitInfo.planName}
        />
      )}
    </div>
  );
}

export default Dashboard;
